import type { ARTryOnTelemetryEventName } from '@zeelool/ar-try-on';
import type { SensorsRuntime } from '../../reliability/manualAnalyticsTypes';

type SensorsInterceptors = Pick<SensorsRuntime, 'registerInterceptor'>;
const installed = new WeakSet<SensorsInterceptors>();
const telemetryEvents = new Set<string>([
  'Tec_ArTryOnSdkInit_Result',
  'Tec_ArTryOnCameraAcquire_Result',
  'Tec_ArTryOnModelLoad_Result',
  'Tec_ArTryOnLoadComplete_Result',
  'Tec_ArTryOnFirstTryOn_Result',
  'Tec_ArTryOnHeadMeshGenerated_Result',
  'Tec_ArTryOnSessionEnd_Result',
] satisfies ARTryOnTelemetryEventName[]);

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function readTimeline(data: unknown): string | undefined {
  if (
    !isRecord(data) ||
    data.type !== 'track' ||
    typeof data.event !== 'string' ||
    !telemetryEvents.has(data.event) ||
    !isRecord(data.properties)
  ) {
    return undefined;
  }
  const value = data.properties.ar_startup_timeline;
  if (typeof value !== 'string') return undefined;
  try {
    const timeline: unknown = JSON.parse(value);
    if (
      Array.isArray(timeline) &&
      timeline.every(
        (entry) =>
          Array.isArray(entry) &&
          (entry.length === 2 || entry.length === 3) &&
          typeof entry[0] === 'string' &&
          typeof entry[1] === 'number' &&
          Number.isFinite(entry[1]) &&
          (entry.length === 2 || (typeof entry[2] === 'number' && Number.isFinite(entry[2]))),
      )
    ) {
      return value;
    }
  } catch {
    // 非 SDK 时间轴结构继续走神策原有属性处理。
  }
  return undefined;
}

/** 神策默认截断 1024 字符；仅保留 AR JSON 时间轴原文，供神策和最终事实镜像共用。 */
export function installArTryOnSensorsTimeline(sensors: SensorsInterceptors): void {
  if (installed.has(sensors) || typeof sensors.registerInterceptor !== 'function') return;
  const timelines = new WeakMap<object, string>();
  sensors.registerInterceptor('buildDataStage', {
    formatData: {
      // 内置 formatData priority=0，在它执行前保存同一事件对象的合法原文。
      priority: -200,
      entry(data: unknown) {
        const timeline = readTimeline(data);
        if (timeline !== undefined && isRecord(data)) timelines.set(data, timeline);
        return data;
      },
    },
    finalAdjustData: {
      // 早于手动埋点关联(-100)和 assembled(0)，两路捕获完整的最终字段。
      priority: -200,
      entry(data: unknown) {
        if (!isRecord(data)) return data;
        const timeline = timelines.get(data);
        timelines.delete(data);
        if (timeline !== undefined && isRecord(data.properties)) {
          data.properties.ar_startup_timeline = timeline;
        }
        return data;
      },
    },
  });
  installed.add(sensors);
}
